import * as React from 'react'
import { clsx } from 'clsx'

import { isHexColor } from '../../helpers/colors'
import { generateTagCssProperties } from './helpers'
import { TAG_COLORS, LEGACY_COLOR_MAP } from './constants'
import type { TagColor } from './constants'

import styles from './Tag.module.css'

const tagColorSet = new Set<string>(TAG_COLORS)

function isTagColor(color: string): color is TagColor {
  return tagColorSet.has(color)
}

type TagProps = {
  /**
   * Label of the tag
   */
  children: string
  /**
   * Color of the tag
   */
  color?: TagColor | string
  /**
   * Additional classname
   */
  className?: string
}

const Tag = React.forwardRef<HTMLSpanElement, TagProps>(
  ({ children, color, className, ...props }: TagProps, forwardedRef) => {
    // Transform legacy hex colors to new predefined colors
    const transformedColor =
      color && LEGACY_COLOR_MAP[color] ? LEGACY_COLOR_MAP[color] : color

    const isPredefinedColor = transformedColor && isTagColor(transformedColor)
    const isCustomColor = transformedColor && isHexColor(transformedColor)

    return (
      <span
        className={clsx(
          styles.base,
          isPredefinedColor && styles[transformedColor],
          className,
        )}
        style={
          isCustomColor ? generateTagCssProperties(transformedColor) : undefined
        }
        ref={forwardedRef}
        {...props}
      >
        <span className={styles.text}>{children}</span>
      </span>
    )
  },
)

Tag.displayName = 'Tag'

export { Tag }
export type { TagProps }
